(() => {
  const init = () => {
    const root = document.querySelector('[data-tc-storefront]');
    if (!root) return;
    root.tcShell?.destroy();
    const controller = new AbortController();
    const signal = controller.signal;
    const on = (el, name, fn) => el?.addEventListener(name, fn, { signal });
    const i18n = JSON.parse(root.querySelector('#tc-shell-i18n')?.textContent || '{}');
    const t = (key, values = {}) => Object.entries(values).reduce((text, [name, value]) => text.split(`{${name}}`).join(String(value)), i18n[key] || key);
    const menu = root.querySelector('[data-menu-toggle]');
    const nav = root.querySelector('#tc-navigation');
    const bag = root.querySelector('[data-bag-modal]');
    const drafts = window.getTCNameDrafts?.(root);
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    let bagOpener;
    const panels = () => [...root.querySelectorAll('[data-panel]')];
    const offsetNames = ['--tc-studio-scroll-offset', '--tc-bag-scroll-offset'];
    const previousOffsets = offsetNames.map(name => ({ name, value: document.documentElement.style.getPropertyValue(name), priority: document.documentElement.style.getPropertyPriority(name) }));
    let shellDestroyed = false;
    const stickyOffset = (selector, edge) => {
      const element = root.querySelector(selector);
      if (!element || element.closest('[hidden]')) return 0;
      const style = getComputedStyle(element);
      const height = element.getBoundingClientRect().height;
      if (style.position !== 'sticky' || style.display === 'none' || style.visibility === 'hidden' || height <= 0) return 0;
      return Math.ceil(height + Math.max(0, parseFloat(style[edge]) || 0) + 12);
    };
    const measureOffsets = () => {
      if (shellDestroyed) return;
      document.documentElement.style.setProperty(offsetNames[0], `${stickyOffset('.tc-builder-preview', 'top')}px`);
      document.documentElement.style.setProperty(offsetNames[1], `${stickyOffset('.tc-sticky-example', 'bottom')}px`);
    };
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measureOffsets) : null;
    root.querySelectorAll('.tc-builder-preview,.tc-sticky-example').forEach(element => observer?.observe(element));
    on(window, 'resize', measureOffsets);
    const focusTarget = element => {
      if (!element) return;
      if (!element.matches('a[href],button,input,select,textarea,summary,[tabindex]')) element.tabIndex = -1;
      element.focus({ preventScroll: true });
    };
    const setMenu = (open, focus = false) => {
      if (!menu || !nav) return;
      menu.setAttribute('aria-expanded', String(open));
      menu.setAttribute('aria-label', t(open ? 'close_menu' : 'open_menu'));
      nav.hidden = !open;
      if (focus) menu.focus();
    };
    const showPage = (page, { scroll = true, hash = true, focus = false } = {}) => {
      const destination = root.querySelector(`[data-panel="${page}"]`);
      if (!destination) return false;
      setMenu(false);
      panels().forEach(panel => panel.hidden = panel.dataset.panel !== page);
      root.dataset.currentPage = page;
      root.dispatchEvent(new CustomEvent('tc:page', { detail: { page }, bubbles: true }));
      if (hash && root.dataset.defaultPage === 'index') history.replaceState(null, '', page === 'product' ? '#create' : location.pathname + location.search);
      if (scroll) root.querySelector('#MainContent')?.scrollIntoView({ block: 'start' });
      if (focus) focusTarget(destination.querySelector('h1') || destination);
      measureOffsets();
      updateVideo();
      return true;
    };
    const updateVideo = () => {
      root.querySelectorAll('.tc-hero-video').forEach(video => {
        if (video.closest('[hidden]') || document.hidden || reducedMotion.matches) video.pause();
        else { video.muted = true; video.play().catch(() => {}); }
      });
      root.querySelectorAll('.tc-product-room-video').forEach(video => {
        if (video.closest('[hidden]') || document.hidden || reducedMotion.matches) video.pause();
        else { video.muted = true; video.play().catch(() => {}); }
      });
    };
    const drawBag = (state = root.tcBuilder?.getState()) => {
      const working = state || drafts?.current();
      if (working) drafts?.save(working);
      const groups = drafts?.list() || [];
      const container = root.querySelector('[data-cart-groups]');
      container.replaceChildren();
      root.querySelector('[data-cart-empty]').hidden = !!groups.length;
      root.querySelector('[data-bag-storage]').hidden = drafts?.storageAvailable() !== false;
      root.querySelector('[data-bag-saved]').hidden = drafts?.storageAvailable() === false;
      const pricing = root.tcDraftPricing;
      const quotes = pricing ? groups.map(group => pricing.quote(group)) : [];
      const subtotal = root.querySelector('[data-draft-subtotal]');
      if (pricing && subtotal) subtotal.textContent = quotes.every(Boolean) ? pricing.money(quotes.reduce((total, quote) => total + quote.total, 0)) : pricing.pending();
      groups.forEach(group => {
        const card = document.createElement('section');
        card.className = 'tc-cart-item';
        card.dataset.nameGroup = group.id;
        const heading = document.createElement('h3');
        heading.dataset.cartName = '';
        heading.id = `tc-name-${group.id}`;
        heading.textContent = group.name;
        card.setAttribute('aria-labelledby', heading.id);
        const count = document.createElement('span');
        count.dataset.cartCount = '';
        count.textContent = `${group.choices.length} ${t('letter_count')}`;
        const lines = document.createElement('div');
        lines.dataset.cartLines = '';
        group.choices.forEach((letter, index) => {
          const line = document.createElement('p');
          line.className = 'tc-bag-line';
          const size = letter.size ? t('size_value', { size: t('size_' + letter.size.toLowerCase()), mm: { Small:80, Medium:120 }[letter.size] }) : t('size_missing');
          line.textContent = `${index + 1} · ${letter.letter} · ${size} · ${letter.colour || t('colour_missing')}`;
          lines.append(line);
        });
        const actions = document.createElement('div');
        actions.className = 'tc-bag-actions';
        const edit = document.createElement('a');
        edit.className = 'tc-text';
        edit.href = bag.dataset.builderUrl;
        edit.dataset.editName = group.id;
        edit.textContent = t('edit');
        edit.setAttribute('aria-label', t('edit_label', { name: group.name }));
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'tc-text';
        remove.dataset.removeName = group.id;
        remove.textContent = t('remove');
        remove.setAttribute('aria-label', t('remove_label', { name: group.name }));
        actions.append(edit, remove);
        card.append(heading, count, lines, actions);
        if (pricing) {
          const total = document.createElement('p');
          total.className = 'tc-commerce-group-total';
          total.dataset.draftNameTotal = '';
          const quote = pricing.quote(group);
          total.textContent = quote ? pricing.money(quote.total) : pricing.pending();
          card.insertBefore(total, actions);
        }
        container.append(card);
      });
    };
    const openBag = state => {
      if (!bag) return;
      if (root.tcCommerceUI) { root.tcCommerceUI.openBag(state); return; }
      drawBag(state);
      root.querySelector('[data-bag-feedback]').textContent = '';
      bagOpener = document.activeElement;
      if (!bag.open) bag.showModal();
    };
    const jump = (target, { focus = false } = {}) => {
      const page = target === 'story' ? 'home' : 'product';
      if (!showPage(page, { scroll: false })) return false;
      if (target === 'colours') root.querySelector('[data-palette]')?.click();
      else {
        const el = root.querySelector(`[data-${target}]`);
        if (el) {
          if (target === 'guide') el.open = true;
          el.scrollIntoView({ block: 'start' });
          if (focus) focusTarget(el.querySelector('summary,h1,h2,h3') || el);
        }
      }
      return true;
    };
    on(root, 'click', event => {
      const el = event.target.closest('button,a');
      if (!el) return;
      if (el.matches('[data-menu-toggle]')) { setMenu(menu.getAttribute('aria-expanded') !== 'true'); return; }
      if (el.matches('[data-bag-open]')) { setMenu(false); openBag(); return; }
      if (el.matches('[data-bag-close]')) { bag.close(); return; }
      if (el.matches('[data-edit-name]')) {
        if (root.tcBuilder?.editName(el.dataset.editName)) {
          event.preventDefault();
          bagOpener = root.querySelector('#tc-demo-name');
          bag.close();
          bagOpener?.focus();
        } else {
          drafts?.activate(el.dataset.editName);
          bag.close();
        }
        return;
      }
      if (el.matches('[data-remove-name]')) {
        const group = drafts?.list().find(item => item.id === el.dataset.removeName);
        if (root.tcBuilder) root.tcBuilder.removeName(el.dataset.removeName);
        else drafts?.remove(el.dataset.removeName);
        drawBag();
        root.querySelector('[data-bag-feedback]').textContent = t('removed', { name: group?.name || '' });
        (root.querySelector('[data-edit-name]') || root.querySelector('[data-bag-another]'))?.focus();
        return;
      }
      if (el.matches('[data-bag-another]')) {
        if (root.tcBuilder) {
          event.preventDefault();
          root.tcBuilder.createAnother();
          bagOpener = root.querySelector('#tc-demo-name');
          bag.close();
          bagOpener?.focus();
        } else {
          drafts?.startAnother();
          bag.close();
        }
        return;
      }
      if (el.dataset.page) {
        if (nav?.contains(el)) setMenu(false, true);
        if (showPage(el.dataset.page, { focus: true })) { event.preventDefault(); if (bag?.open) { bagOpener = root.querySelector('#tc-demo-name'); bag.close(); } }
      }
      if (el.dataset.jump) { setMenu(false, true); if (jump(el.dataset.jump, { focus: true })) event.preventDefault(); }
      if (el.dataset.policy) {
        const modal = root.querySelector('[data-modal]');
        const content = root.querySelector('[data-modal-content]');
        content.replaceChildren();
        const heading = document.createElement('h2'); heading.textContent = el.textContent;
        heading.id = 'tc-modal-title';
        modal.setAttribute('aria-labelledby', heading.id);
        modal.removeAttribute('aria-label');
        const body = document.createElement('p'); body.textContent = t('policy_pending');
        content.append(heading, body); modal.showModal();
      }
      if (el.matches('[data-close]')) root.querySelector('[data-modal]')?.close();
    });
    on(root, 'tc:open-bag', event => openBag(event.detail));
    on(root, 'tc:request-page', event => showPage(event.detail.page));
    on(root, 'tc:selection-change', event => { if (bag?.open && !root.tcCommerceUI) drawBag(event.detail); });
    on(root, 'keydown', event => { if (event.key === 'Escape' && nav && !nav.hidden) { setMenu(false, true); event.preventDefault(); } });
    on(document, 'click', event => { if (!event.target.closest('[data-menu-wrapper]')) setMenu(false); });
    on(root.querySelector('[data-menu-wrapper]'), 'focusout', event => { if (!event.currentTarget.contains(event.relatedTarget)) setMenu(false); });
    on(bag, 'click', event => { if (event.target === bag) bag.close(); });
    on(bag, 'close', () => bagOpener?.isConnected && bagOpener.focus());
    on(document, 'visibilitychange', updateVideo);
    on(reducedMotion, 'change', updateVideo);
    const handleHash = () => {
      const hash = location.hash.slice(1);
      if (hash === 'create') showPage('product', { scroll: false, hash: false });
      else if (['colours','guide','story','help'].includes(hash)) jump(hash);
      else if (!hash && root.dataset.defaultPage === 'index') showPage('home', { scroll: false, hash: false });
    };
    on(window, 'hashchange', handleHash);
    root.tcShell = { showPage, openBag, destroy: () => {
      shellDestroyed = true;
      controller.abort();
      observer?.disconnect();
      previousOffsets.forEach(({ name, value, priority }) => {
        if (value) document.documentElement.style.setProperty(name, value, priority);
        else document.documentElement.style.removeProperty(name);
      });
    } };
    const assets = JSON.parse(root.querySelector('#tc-photo-assets')?.textContent || '{}');
    if (assets.sheets?.C) root.style.setProperty('--tc-sheet', `url("${assets.sheets.C}")`);
    handleHash(); measureOffsets(); updateVideo();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
  document.addEventListener('shopify:section:load', init);
})();
